<script setup>
/******************************************************************************
 * PageComponent: MeterRegister (Zählwerk) reading value: single input
 *****************************************************************************/
import { computed } from "vue";
import { useReadingStore } from "Stores/reading";
import { parseReadingInput, validateKeyDown } from "@/handlers/useReading";
const data = useReadingStore();
const props = defineProps({
    meterRegisterId: {
        type: String,
        required: true,
    },
});
const emit = defineEmits(["change"]);
const meterRegisterData = data.getMeterRegisterData(props.meterRegisterId);
const readingValue = computed(() =>
    data.getReadingValue(props.meterRegisterId)
);
/**
 * @function meter without decimals: keep the digits before the comma, typed or pasted.
 * @param {String} text
 * @returns {String} cleaned value
 */
const setValue = (text) => {
    const value = parseReadingInput(text).pre.slice(
        0,
        meterRegisterData.preCommaDigits
    );
    emit("change", value);
    return value;
};
/**
 * @function on key down, check if the pressed key is valid
 * @param {KeyboardEvent} event
 */
const onKeyDown = (event) =>
    validateKeyDown(
        event,
        meterRegisterData.preCommaDigits,
        readingValue.value.toString().length
    );
/**
 * @function on input (typing, pasting, autofill), take over the cleaned value
 * and show it in the input.
 * @param {InputEvent} event
 */
const onInput = (event) => (event.target.value = setValue(event.target.value));
/**
 * @function on paste, read the whole clipboard text - the browser would cut it at maxlength.
 * @param {ClipboardEvent} event
 */
const onPaste = (event) => {
    event.preventDefault();
    setValue(event.clipboardData?.getData("text") ?? "");
};
</script>

<template>
    <div class="post">
        <input
            :id="`meterReading-${meterRegisterId}`"
            :value="readingValue"
            class="form-input form-input--has-addon form-input--meter"
            type="text"
            inputmode="numeric"
            :maxlength="meterRegisterData.preCommaDigits"
            formnovalidate
            @keydown="onKeyDown"
            @input="onInput"
            @paste="onPaste"
        />
        <ul class="digits">
            <li
                v-for="index in meterRegisterData.preCommaDigits"
                :key="index"
                class="digit"
            />
        </ul>
        <div class="form-input-addon form-input-addon--meter">
            {{ meterRegisterData.meterUnit }}
        </div>
    </div>
</template>

<style lang="scss" scoped>
$addon-width: 46px;

input {
    z-index: get($z, "form");
}

.digits {
    padding-right: #{$addon-width + 19px};
}

.post {
    display: flex;
    position: relative;

    width: 100%;

    .form-input {
        flex-grow: 1;
    }

    .form-input-addon {
        flex: 0 0 $addon-width;
    }

    .digits {
        padding-right: #{$addon-width + 19px};
    }
}

input[type="text"] {
    z-index: get($z, "form");

    width: 100%;
}
</style>
